import type { JsonValue } from '@mainstage/types';
import type {
  ConfigurationApi,
  GetArgs,
  KeyArgs,
  NamespaceArgs,
  RequestOptions,
  SetArgs,
} from './api/ConfigurationApi';
import type { ConfigurationSchema } from './types';
import type { DiscoveryApi, FetchApi } from '@mainstage/core-plugin-api';
import crossFetch from 'cross-fetch';
import {
  CustomError,
  MultipleCustomErrors,
  encodeQueryParams,
  handleFailedResponse,
} from '@agilelab/plugin-wb-platform-common';

type GetConfigResponse = {
  value: string;
};

export class ConfigurationClient implements ConfigurationApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('wb-configuration');
  }

  async getConfigSchema(
    { namespace }: NamespaceArgs,
    options?: RequestOptions,
  ): Promise<ConfigurationSchema | undefined> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/configs/${namespace}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );

    if (response.status === 404) return undefined;
    await handleFailedResponse(response);
    return (await response.json()) as ConfigurationSchema;
  }

  async has(args: GetArgs, options?: RequestOptions): Promise<boolean> {
    // TODO: Implement has endpoint backend-side
    const value = await this.get(args, options);
    return value !== undefined;
  }

  async get<T = JsonValue>(
    { namespace, key, noCache }: GetArgs,
    options?: RequestOptions,
  ): Promise<T | undefined> {
    const baseUrl = await this.getBaseUrl();
    const queryParams = encodeQueryParams({
      'no-cache': noCache,
    });

    const response = await this.fetchApi.fetch(
      `${baseUrl}/configs/${namespace}/${key}${queryParams}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );

    if (response.status === 404) return undefined;

    await handleFailedResponse(response);

    const { value }: GetConfigResponse = await response.json();

    return JSON.parse(value) as T;
  }

  async set(
    { namespace, key, value }: SetArgs,
    options?: RequestOptions,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const response = await this.fetchApi.fetch(
      `${baseUrl}/configs/${namespace}/${key}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
        body: JSON.stringify({ value: JSON.stringify(value) }),
      },
    );

    if (!response.ok) {
      let json = null;

      try {
        json = await response.json();
      } catch (error) {
        throw new CustomError(
          'Unable to parse the response received',
          error.message,
        );
      }

      if (json && 'details' in json) {
        const issues = Array.isArray(json.details?.issues)
          ? json.details.issues
          : [];
        throw new MultipleCustomErrors(issues);
      }

      if (json && 'error' in json) {
        throw new CustomError(json.error.name, json.error.message);
      }

      throw new CustomError(json?.message ?? 'Unknown error', '');
    }
  }

  async clear(
    { namespace, key }: KeyArgs,
    options?: RequestOptions,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/configs/${namespace}/${key}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options.token}` }),
        },
      },
    );
    await handleFailedResponse(response);
  }
}
